import { useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { RECOVERY_EXECUTIONS } from "@/data/mock";

/* RCV-04 — Recovery Execution Monitor.
   Canonical source: prototype/pages/recovery/execution.html (no
   current Figma frame located). Three source-defined states —
   "In Progress", "Complete", "Failed" — each its own independent
   static fixture (REC-2026-114/113/115), matching the source's own
   separate example ids for each state rather than one thread that
   changes over time. Per DD-006, no live progression is simulated
   between states; this mirrors the LND-01→LND-00 non-interaction
   precedent already established elsewhere in this engagement. */

type View = "in-progress" | "complete" | "failed";

const TABS: { key: View; label: string }[] = [
  { key: "in-progress", label: "In Progress" },
  { key: "complete", label: "Complete" },
  { key: "failed", label: "Failed" },
];

const STEP_TONE: Record<string, string> = {
  done: "bg-success text-white",
  active: "bg-primary text-white",
  pending: "border border-line-strong text-muted",
  failed: "bg-danger text-white",
};

export function RecoveryExecution() {
  const [view, setView] = useState<View>("in-progress");
  const exec = RECOVERY_EXECUTIONS[view];

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-extrabold text-ink">Recovery Execution — {exec.id}</h1>
          <p className="text-sm text-muted">{exec.method} · Quorum {exec.quorum}</p>
        </div>
        <div className="flex gap-1.5">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setView(t.key)}
              className={`rounded-[6px] border px-3 py-1.5 text-xs font-semibold ${view === t.key ? "border-primary bg-primary text-on-primary" : "border-line bg-surface text-muted"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {exec.failureBanner && (
        <div className="mb-5 rounded-md border border-danger-border bg-danger-soft px-4 py-3 text-sm text-danger">{exec.failureBanner}</div>
      )}

      <div className="mb-5 rounded-[10px] border border-line bg-surface p-6">
        <div className="mb-4 flex items-center justify-between">
          <Badge variant={exec.status === "Complete" ? "success" : exec.status === "Failed" ? "danger" : "info"}>{exec.status}</Badge>
          <span className="font-mono text-xs text-muted">{exec.keyFingerprint}{exec.duration ? ` · ${exec.duration}` : ""}</span>
        </div>

        <div className="mb-5 h-2 overflow-hidden rounded-full bg-bg">
          <div
            className={`h-full rounded-full ${exec.status === "Failed" ? "bg-danger" : "bg-primary"}`}
            style={{ width: `${exec.progressPct}%` }}
          />
        </div>

        <ol className="flex flex-wrap gap-3">
          {exec.steps.map((s, i) => (
            <li key={s.label} className="flex items-center gap-2">
              <span aria-hidden className={`grid size-6 shrink-0 place-items-center rounded-full text-[11px] font-bold ${STEP_TONE[s.state]}`}>
                {s.state === "done" ? "✓" : s.state === "failed" ? "✗" : i + 1}
              </span>
              <span className={`text-xs font-semibold ${s.state === "pending" ? "text-muted" : "text-ink"}`}>{s.label}</span>
            </li>
          ))}
        </ol>
      </div>

      {exec.log.length > 0 && (
        <div className="overflow-hidden rounded-[10px] border border-line bg-console-bg">
          <div className="border-b border-line bg-console-panel px-4 py-2 font-mono text-xs text-console-muted">Execution Log</div>
          <ul className="p-4 font-mono text-[13px] leading-relaxed">
            {exec.log.map((l, i) => (
              <li key={i} className={l.danger ? "text-console-danger" : "text-console-text"}>
                <span className="text-console-muted">{l.time}</span> {l.text}
              </li>
            ))}
          </ul>
        </div>
      )}

      {view === "complete" && (
        <div className="mt-5">
          <Link to="/recovery/audit" className="text-sm font-semibold text-primary hover:underline">View audit packet →</Link>
        </div>
      )}
    </div>
  );
}
